import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ClientLogo } from '@/components/ClientLogo';
import { clientsRow1, clientsRow2 } from '@/data/clients';
import { 
    Shield, ShieldCheck, Users, Award, Clock, Target, 
    ChevronRight, Star, Phone, CheckCircle2,
    Building2, Factory, Calendar, UserCheck, GraduationCap
} from 'lucide-react';

const stats = [
    { value: '15+', label: 'Years Experience', icon: Clock },
    { value: '500+', label: 'Active Guards', icon: Users },
    { value: '200+', label: 'Happy Clients', icon: Award },
    { value: '365', label: 'Days of Protection', icon: Shield },
];

const services = [
    {
        icon: ShieldCheck,
        title: 'Armed Guards',
        description: 'Highly trained armed security personnel with valid gun licenses for maximum protection.',
        image: '/images/f1.jpg',
    },
    {
        icon: Building2,
        title: 'Corporate Security',
        description: 'Comprehensive security solutions for offices, corporate buildings, and business establishments.',
        image: '/images/f2.jpg',
    },
    {
        icon: UserCheck,
        title: 'VIP Protection',
        description: 'Executive protection services for high-profile individuals requiring discreet security.',
        image: '/images/f3.jpg',
    },
    {
        icon: Factory,
        title: 'Industrial Security',
        description: 'Specialized security for manufacturing plants, warehouses, and industrial facilities.',
        image: '/images/f4.jpg',
    },
    {
        icon: Calendar,
        title: 'Event Security',
        description: 'Professional event security management for functions, exhibitions, and large gatherings.',
        image: '/images/f5.jpg',
    },
    {
        icon: GraduationCap,
        title: 'Security Training',
        description: 'Comprehensive training programs for security personnel covering all aspects of protection.',
        image: '/images/f10.jpg',
    },
];

const features = [
    'Ex-Army & JCO Personnel',
    'PSARA Licensed Company',
    '24/7 Manned Control Rooms',
    'Rapid Response Team',
    'Regular Training Programs',
    'Bangalore-Based Operations',
];

const testimonials = [
    {
        name: 'Rajesh Kumar',
        role: 'CEO, Tech Solutions Pvt Ltd',
        content: 'Shiva Security has been providing security services to our corporate office for over 3 years. Their guards are well-trained, professional, and always punctual.',
        rating: 5,
    },
    {
        name: 'Priya Sharma',
        role: 'HR Manager, Manufacturing Inc',
        content: 'The industrial security team from Shiva Security understands our facility requirements perfectly. They have significantly improved our premises security.',
        rating: 5,
    },
    {
        name: 'Vikram Singh',
        role: 'Event Manager',
        content: 'We regularly hire Shiva Security for our large-scale events. Their coordination and crowd management skills are exceptional.',
        rating: 5,
    },
];

export const HomePage = () => {
    const [activeTestimonial, setActiveTestimonial] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="overflow-hidden">
            {/* Hero Section - Premium Split Layout */}
            <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
                {/* Background Layer - Seamless blend, NO divider */}
                <div className="absolute inset-0">
                    {/* Full background gradient */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-sky-50/90 to-sky-100/50 dark:from-[hsl(215,70%,6%)] dark:via-[hsl(215,65%,8%)] dark:to-[hsl(215,60%,10%)]" />
                    
                    {/* Security Guards Image - Seamless full background with fade */}
                    <div className="hidden lg:block absolute inset-0">
                        {/* Image layer - full width with opacity */}
                        <div 
                            className="absolute inset-0 bg-cover bg-no-repeat"
                            style={{ 
                                backgroundImage: 'url(https://customer-assets.emergentagent.com/job_sentinel-watch-6/artifacts/eacsktff_image.png)',
                                backgroundPosition: 'center 30%',
                                backgroundSize: 'cover',
                                filter: 'saturate(0.7)',
                                opacity: '0.35'
                            }}
                        />
                        {/* Left-to-right fade - seamless, NO hard edge */}
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-50 from-[5%] via-blue-50/95 via-[35%] to-transparent to-[65%] dark:from-[hsl(215,70%,6%)] dark:via-[hsl(215,70%,6%)]/95 dark:to-transparent" />
                        {/* Navy tint overlay on image for premium look */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent from-[40%] to-[hsl(215,60%,20%)]/15 dark:to-[hsl(215,60%,15%)]/30" />
                    </div>
                    
                    {/* Om Symbol Watermark - Very subtle */}
                    <div 
                        className="absolute inset-0 opacity-[0.02] dark:opacity-[0.025] pointer-events-none"
                        style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ctext x='50' y='60' font-size='50' text-anchor='middle' fill='%231e3a5f' font-family='serif'%3Eॐ%3C/text%3E%3C/svg%3E")`,
                            backgroundSize: '180px 180px',
                            backgroundPosition: '8% 25%',
                            backgroundRepeat: 'no-repeat'
                        }}
                    />
                    
                    {/* Subtle saffron accent glow at bottom corner only */}
                    <div className="absolute bottom-0 right-0 w-[35%] h-[25%] bg-gradient-to-tl from-orange-400/10 via-orange-300/5 to-transparent dark:from-orange-500/8 dark:via-orange-400/3 dark:to-transparent blur-lg" />
                </div>

                {/* Mobile Background */}
                <div className="lg:hidden absolute inset-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-sky-50 to-white dark:from-[hsl(215,70%,6%)] dark:via-[hsl(215,65%,8%)] dark:to-[hsl(215,60%,10%)]" />
                    <div 
                        className="absolute inset-0 bg-cover bg-center"
                        style={{ 
                            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_sentinel-watch-6/artifacts/eacsktff_image.png)',
                            opacity: '0.06',
                            filter: 'saturate(0.6)'
                        }}
                    />
                </div>

                {/* Content Container */}
                <div className="relative z-10 w-full pt-24 lg:pt-28 pb-12">
                    <div className="container mx-auto px-4 lg:px-8">
                        <div className="flex flex-col lg:flex-row lg:items-center min-h-[calc(100vh-8rem)]">
                            {/* Left Content Zone - Clean, no image behind */}
                            <div className="lg:w-[50%] lg:pr-12 py-8 lg:py-0">
                                {/* PSARA Badge - Saffron accent */}
                                <div className="inline-flex items-center gap-2 bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 px-4 py-2 rounded-full text-sm font-semibold mb-8 animate-fade-in border border-orange-200/60 dark:border-orange-500/20" data-testid="psara-badge">
                                    <Shield className="w-4 h-4" />
                                    PSARA Licensed Security Provider
                                </div>
                                
                                {/* Main Headline - Dark navy with saffron highlight */}
                                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[0.92] mb-6 animate-fade-in animation-delay-100" data-testid="hero-headline">
                                    <span className="text-[hsl(215,65%,15%)] dark:text-white block">PROTECTING WHAT</span>
                                    <span className="text-orange-500 dark:text-orange-400 block">MATTERS MOST</span>
                                </h1>
                                
                                {/* Description - High contrast */}
                                <p className="text-[hsl(215,40%,30%)] dark:text-gray-300 text-base lg:text-lg mb-8 leading-relaxed animate-fade-in animation-delay-200 max-w-lg font-medium" data-testid="hero-description">
                                    Shiva Security Services delivers comprehensive security solutions across Bangalore. 
                                    From armed guards to corporate protection, we ensure your safety with highly trained professionals.
                                </p>
                                
                                {/* CTA Buttons - Primary saffron, Secondary navy outline */}
                                <div className="flex flex-col sm:flex-row gap-4 animate-fade-in animation-delay-300">
                                    <Link to="/contact">
                                        <Button 
                                            className="bg-orange-500 hover:bg-orange-600 dark:bg-orange-500 dark:hover:bg-orange-400 text-white rounded-lg px-8 py-6 text-lg font-semibold w-full sm:w-auto shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/30 transition-all duration-300"
                                            data-testid="get-quote-btn"
                                        >
                                            Get Free Quote
                                            <ChevronRight className="w-5 h-5 ml-2" />
                                        </Button>
                                    </Link>
                                    <Link to="/services">
                                        <Button 
                                            variant="outline" 
                                            className="rounded-lg px-8 py-6 text-lg font-semibold border-2 border-[hsl(215,50%,25%)] dark:border-gray-500 text-[hsl(215,60%,18%)] dark:text-white hover:bg-[hsl(215,60%,18%)]/5 dark:hover:bg-white/5 w-full sm:w-auto bg-white/70 dark:bg-transparent"
                                            data-testid="services-btn"
                                        >
                                            Our Services
                                        </Button>
                                    </Link>
                                </div>

                                {/* Trust Indicators - Minimal icons */}
                                <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 animate-fade-in animation-delay-400">
                                    <div className="flex items-center gap-2" data-testid="trust-licensed">
                                        <CheckCircle2 className="w-4 h-4 text-orange-500 dark:text-orange-400" />
                                        <span className="text-[hsl(215,40%,30%)] dark:text-gray-400 text-sm font-medium">Licensed & Insured</span>
                                    </div>
                                    <div className="flex items-center gap-2" data-testid="trust-support">
                                        <CheckCircle2 className="w-4 h-4 text-orange-500 dark:text-orange-400" />
                                        <span className="text-[hsl(215,40%,30%)] dark:text-gray-400 text-sm font-medium">Rapid Response Team</span>
                                    </div>
                                    <div className="flex items-center gap-2" data-testid="trust-coverage">
                                        <CheckCircle2 className="w-4 h-4 text-orange-500 dark:text-orange-400" />
                                        <span className="text-[hsl(215,40%,30%)] dark:text-gray-400 text-sm font-medium">Bangalore-Based Operations</span>
                                    </div>
                                </div>
                            </div>

                            {/* Right Side - Stats Cards with Glassmorphism */}
                            <div className="hidden lg:flex lg:w-[50%] flex-col items-end justify-center gap-5 pr-4 xl:pr-8 animate-fade-in animation-delay-500">
                                {/* Stat Card 1 - Active Guards */}
                                <div className="bg-white/70 dark:bg-[hsl(215,50%,12%)]/70 backdrop-blur-2xl rounded-2xl p-5 w-60 shadow-lg shadow-black/5 dark:shadow-black/20 border border-white/60 dark:border-gray-700/40 transform translate-x-6 hover:translate-x-4 transition-transform duration-300" data-testid="stat-guards">
                                    <div className="flex items-center gap-4">
                                        <div className="w-11 h-11 rounded-xl bg-orange-100 dark:bg-orange-500/15 flex items-center justify-center">
                                            <Target className="w-5 h-5 text-orange-500 dark:text-orange-400" />
                                        </div>
                                        <div>
                                            <p className="font-display text-3xl text-[hsl(215,65%,15%)] dark:text-white">500+</p>
                                            <p className="text-[hsl(215,25%,45%)] dark:text-gray-400 text-sm">Active Guards</p>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Stat Card 2 - Happy Clients */}
                                <div className="bg-white/70 dark:bg-[hsl(215,50%,12%)]/70 backdrop-blur-2xl rounded-2xl p-5 w-60 shadow-lg shadow-black/5 dark:shadow-black/20 border border-white/60 dark:border-gray-700/40 transform -translate-x-4 hover:-translate-x-6 transition-transform duration-300" data-testid="stat-clients">
                                    <div className="flex items-center gap-4">
                                        <div className="w-11 h-11 rounded-xl bg-orange-100 dark:bg-orange-500/15 flex items-center justify-center">
                                            <Users className="w-5 h-5 text-orange-500 dark:text-orange-400" />
                                        </div>
                                        <div>
                                            <p className="font-display text-3xl text-[hsl(215,65%,15%)] dark:text-white">200+</p>
                                            <p className="text-[hsl(215,25%,45%)] dark:text-gray-400 text-sm">Happy Clients</p>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Stat Card 3 - Years Experience */}
                                <div className="bg-white/70 dark:bg-[hsl(215,50%,12%)]/70 backdrop-blur-2xl rounded-2xl p-5 w-60 shadow-lg shadow-black/5 dark:shadow-black/20 border border-white/60 dark:border-gray-700/40 transform translate-x-6 hover:translate-x-4 transition-transform duration-300" data-testid="stat-experience">
                                    <div className="flex items-center gap-4">
                                        <div className="w-11 h-11 rounded-xl bg-orange-100 dark:bg-orange-500/15 flex items-center justify-center">
                                            <Clock className="w-5 h-5 text-orange-500 dark:text-orange-400" />
                                        </div>
                                        <div>
                                            <p className="font-display text-3xl text-[hsl(215,65%,15%)] dark:text-white">15+</p>
                                            <p className="text-[hsl(215,25%,45%)] dark:text-gray-400 text-sm">Years Experience</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Mobile Stats Cards */}
                            <div className="lg:hidden grid grid-cols-3 gap-3 mt-8 animate-fade-in animation-delay-500">
                                <div className="bg-white/85 dark:bg-[hsl(215,50%,12%)]/85 backdrop-blur-xl rounded-xl p-4 text-center shadow-md border border-white/50 dark:border-gray-700/40" data-testid="mobile-stat-guards">
                                    <Target className="w-5 h-5 text-orange-500 dark:text-orange-400 mx-auto mb-2" />
                                    <p className="font-display text-2xl text-[hsl(215,65%,15%)] dark:text-white">500+</p>
                                    <p className="text-[hsl(215,25%,45%)] dark:text-gray-400 text-xs">Guards</p>
                                </div>
                                <div className="bg-white/85 dark:bg-[hsl(215,50%,12%)]/85 backdrop-blur-xl rounded-xl p-4 text-center shadow-md border border-white/50 dark:border-gray-700/40" data-testid="mobile-stat-clients">
                                    <Users className="w-5 h-5 text-orange-500 dark:text-orange-400 mx-auto mb-2" />
                                    <p className="font-display text-2xl text-[hsl(215,65%,15%)] dark:text-white">200+</p>
                                    <p className="text-[hsl(215,25%,45%)] dark:text-gray-400 text-xs">Clients</p>
                                </div>
                                <div className="bg-white/85 dark:bg-[hsl(215,50%,12%)]/85 backdrop-blur-xl rounded-xl p-4 text-center shadow-md border border-white/50 dark:border-gray-700/40" data-testid="mobile-stat-experience">
                                    <Clock className="w-5 h-5 text-orange-500 dark:text-orange-400 mx-auto mb-2" />
                                    <p className="font-display text-2xl text-[hsl(215,65%,15%)] dark:text-white">15+</p>
                                    <p className="text-[hsl(215,25%,45%)] dark:text-gray-400 text-xs">Years</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scroll Indicator */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce hidden lg:flex">
                    <div className="w-8 h-12 rounded-full border-2 border-[hsl(215,30%,70%)] dark:border-gray-600 flex items-start justify-center p-2">
                        <div className="w-1.5 h-3 bg-amber-500 rounded-full" />
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-12 lg:py-0 lg:-mt-16 relative z-20 px-4 lg:px-8">
                <div className="container mx-auto">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {stats.map((stat, index) => (
                            <Card 
                                key={index} 
                                className="card-elevated p-6 lg:p-8 text-center group hover:border-accent/30 transition-all duration-500"
                                style={{ animationDelay: `${index * 100}ms` }}
                            >
                                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors duration-300">
                                    <stat.icon className="w-7 h-7 text-accent" />
                                </div>
                                <p className="font-display text-4xl lg:text-5xl text-foreground mb-2">{stat.value}</p>
                                <p className="text-muted-foreground text-sm">{stat.label}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Valued Clients */}
            <section className="py-16 lg:py-24 overflow-hidden">
                <div className="container mx-auto px-4 lg:px-8 mb-10">
                    <div className="text-center">
                        <Badge className="bg-accent/10 text-accent border-accent/20 mb-4">
                            TRUSTED BY
                        </Badge>
                        <h2 className="font-display text-3xl lg:text-4xl text-foreground">
                            OUR VALUED CLIENTS
                        </h2>
                        <div className="mt-4">
                            <Link to="/clients">
                                <Button variant="outline" className="rounded-lg px-6 py-3 font-semibold">
                                    Know More
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
                
                {/* Scrolling Logos - Row 1 (Left to Right) */}
                <div className="relative mb-6 w-screen left-1/2 -translate-x-1/2">
                    <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-background to-transparent z-10" />
                    <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-background to-transparent z-10" />
                    
                    <div className="flex w-max animate-scroll-left">
                        {[...Array(2)].map((_, setIndex) => (
                            <div key={setIndex} className="flex items-center gap-12 px-6">
                                {clientsRow1.map((client) => (
                                    <div key={`${setIndex}-${client.name}`} className="flex-shrink-0 w-44 h-24 bg-white/80 dark:bg-[hsl(215,50%,12%)]/80 rounded-xl flex items-center justify-center border border-border/50 hover:border-accent/30 transition-colors p-1">
                                        <ClientLogo
                                            name={client.name}
                                            logo={client.logo}
                                            imgClassName="w-full h-full object-contain"
                                            textClassName="font-display text-sm text-muted-foreground text-center leading-tight"
                                        />
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
                
                {/* Scrolling Logos - Row 2 (Right to Left) */}
                <div className="relative w-screen left-1/2 -translate-x-1/2">
                    <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-background to-transparent z-10" />
                    <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-background to-transparent z-10" />
                    
                    <div className="flex w-max animate-scroll-right">
                        {[...Array(2)].map((_, setIndex) => (
                            <div key={setIndex} className="flex items-center gap-12 px-6">
                                {clientsRow2.map((client) => (
                                    <div key={`${setIndex}-${client.name}`} className="flex-shrink-0 w-44 h-24 bg-white/80 dark:bg-[hsl(215,50%,12%)]/80 rounded-xl flex items-center justify-center border border-border/50 hover:border-accent/30 transition-colors p-1">
                                        <ClientLogo
                                            name={client.name}
                                            logo={client.logo}
                                            imgClassName="w-full h-full object-contain"
                                            textClassName="font-display text-sm text-muted-foreground text-center leading-tight"
                                        />
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-20 lg:py-32 px-4 lg:px-8">
                <div className="container mx-auto">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <Badge className="bg-accent/10 text-accent border-accent/20 mb-4">
                            OUR SERVICES
                        </Badge>
                        <h2 className="font-display text-4xl lg:text-5xl text-foreground mb-6">
                            COMPREHENSIVE SECURITY SOLUTIONS
                        </h2>
                        <p className="text-muted-foreground text-lg">
                            From personal protection to industrial security, we offer a wide range of 
                            professional security services tailored to your specific needs.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                        {services.map((service, index) => (
                            <Card 
                                key={index}
                                className="card-elevated overflow-hidden group cursor-pointer"
                            >
                                <div className="relative h-48 overflow-hidden">
                                    <img 
                                        src={service.image} 
                                        alt={service.title}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
                                    <div className="absolute bottom-4 left-4">
                                        <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center shadow-lg">
                                            <service.icon className="w-6 h-6 text-accent-foreground" />
                                        </div>
                                    </div>
                                </div>
                                <CardContent className="p-6">
                                    <h3 className="font-display text-2xl text-foreground mb-3">{service.title}</h3>
                                    <p className="text-muted-foreground text-sm leading-relaxed mb-4">{service.description}</p>
                                    <Link 
                                        to="/services"
                                        className="inline-flex items-center text-accent font-medium text-sm hover:gap-3 transition-all duration-300"
                                    >
                                        Learn More
                                        <ChevronRight className="w-4 h-4 ml-1" />
                                    </Link>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    <div className="text-center mt-12">
                        <Link to="/services">
                            <Button className="btn-primary rounded-lg px-8 py-6 text-lg font-semibold">
                                View All Services
                                <ChevronRight className="w-5 h-5 ml-2" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Why Choose Us Section */}
            <section className="py-20 lg:py-32 bg-muted/50 px-4 lg:px-8">
                <div className="container mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                        {/* Left Content */}
                        <div>
                            <Badge className="bg-accent/10 text-accent border-accent/20 mb-4">
                                WHY CHOOSE US
                            </Badge>
                            <h2 className="font-display text-4xl lg:text-5xl text-foreground mb-6">
                                TRUSTED SECURITY <span className="text-gradient-gold">EXCELLENCE</span>
                            </h2>
                            <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                                With over 15 years of experience, Shiva Security Services stands as a leader 
                                in providing comprehensive security solutions. Our team of ex-army personnel 
                                and highly trained guards ensures your safety with unmatched expertise.
                            </p>

                            <div className="grid grid-cols-2 gap-4 mb-8">
                                {features.map((feature, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                                            <CheckCircle2 className="w-5 h-5 text-accent" />
                                        </div>
                                        <span className="text-foreground text-sm font-medium">{feature}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/about">
                                    <Button className="btn-accent rounded-lg px-8 py-6 font-semibold w-full sm:w-auto">
                                        About Us
                                        <ChevronRight className="w-5 h-5 ml-2" />
                                    </Button>
                                </Link>
                                <a href="tel:+919606924394">
                                    <Button 
                                        variant="outline" 
                                        className="rounded-lg px-8 py-6 font-semibold w-full sm:w-auto"
                                    >
                                        <Phone className="w-5 h-5 mr-2" />
                                        Call Us Now
                                    </Button>
                                </a>
                            </div>
                        </div>

                        {/* Right Image Grid */}
                        <div className="relative">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-4">
                                    <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                                        <img 
                                            src="/images/h1.jpg"
                                            alt="Corporate Security"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="aspect-square rounded-2xl bg-accent flex items-center justify-center p-6">
                                        <div className="text-center">
                                            <p className="font-display text-5xl text-accent-foreground mb-2">15+</p>
                                            <p className="text-accent-foreground/80 text-sm">Years of Excellence</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-4 pt-8">
                                    <div className="aspect-square rounded-2xl bg-hero-pattern flex items-center justify-center p-6">
                                        <div className="text-center">
                                            <p className="font-display text-5xl text-primary-foreground mb-2">200+</p>
                                            <p className="text-primary-foreground/80 text-sm">Trusted Clients</p>
                                        </div>
                                    </div>
                                    <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                                        <img 
                                            src="/images/h2.jpg"
                                            alt="Security Team"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="py-20 lg:py-32 px-4 lg:px-8">
                <div className="container mx-auto">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <Badge className="bg-accent/10 text-accent border-accent/20 mb-4">
                            TESTIMONIALS
                        </Badge>
                        <h2 className="font-display text-4xl lg:text-5xl text-foreground mb-6">
                            WHAT OUR CLIENTS SAY
                        </h2>
                        <p className="text-muted-foreground text-lg">
                            Do not just take our word for it. Here is what our valued clients 
                            have to say about our security services.
                        </p>
                    </div>

                    <div className="max-w-4xl mx-auto">
                        <Card className="card-elevated p-8 lg:p-12 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-32 h-32 bg-accent/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
                            <div className="absolute bottom-0 right-0 w-48 h-48 bg-accent/5 rounded-full translate-x-1/4 translate-y-1/4" />
                            
                            <div className="relative z-10">
                                <div className="flex items-center justify-center gap-1 mb-6">
                                    {[...Array(5)].map((_, i) => (
                                        <Star 
                                            key={i} 
                                            className="w-6 h-6 text-accent fill-accent"
                                        />
                                    ))}
                                </div>
                                
                                <blockquote className="text-center text-xl lg:text-2xl text-foreground leading-relaxed mb-8 font-medium">
                                    {testimonials[activeTestimonial].content}
                                </blockquote>
                                
                                <div className="text-center">
                                    <p className="font-display text-xl text-foreground">
                                        {testimonials[activeTestimonial].name}
                                    </p>
                                    <p className="text-muted-foreground text-sm">
                                        {testimonials[activeTestimonial].role}
                                    </p>
                                </div>

                                <div className="flex items-center justify-center gap-2 mt-8">
                                    {testimonials.map((_, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setActiveTestimonial(index)}
                                            className={`w-3 h-3 rounded-full transition-all duration-300 ${
                                                index === activeTestimonial 
                                                    ? 'bg-accent w-8' 
                                                    : 'bg-muted hover:bg-muted-foreground/30'
                                            }`}
                                            aria-label={`Go to testimonial ${index + 1}`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Emergency CTA */}
            <section className="py-16 bg-destructive/5 border-y border-destructive/20 px-4 lg:px-8">
                <div className="container mx-auto">
                    <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
                        <div className="flex items-center gap-6">
                            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center animate-pulse">
                                <Phone className="w-8 h-8 text-destructive" />
                            </div>
                            <div>
                                <p className="text-muted-foreground text-sm mb-1">24/7 Emergency Hotline</p>
                                <p className="font-display text-3xl lg:text-4xl text-foreground">+91-9606924394</p>
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <a href="tel:+919606924394">
                                <Button className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-lg px-8 py-6 text-lg font-semibold">
                                    <Phone className="w-5 h-5 mr-2" />
                                    Call Emergency Line
                                </Button>
                            </a>
                            <Link to="/contact">
                                <Button variant="outline" className="rounded-lg px-8 py-6 text-lg font-semibold">
                                    Request Callback
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
